/**
 * THE FIVE REALITIES (`what-can-start-2`).
 *
 * OMNΛS can now say five different things, and this module exists so a screen
 * cannot compress them into green and red:
 *
 *   YES                  a gate PASSED on canonical evidence
 *   NO                   a gate FAILED — an established negative
 *   WE DO NOT KNOW       UNKNOWN — the platform cannot establish yes or no
 *   SOURCES DISAGREE     UNKNOWN + `conflict: true`
 *   OMNΛS IS MISSING     UNKNOWN + `platformGap: "<token>"`
 *
 * Plus a sixth that is now rare: NOT_REQUIRED means EXPLICIT applicability
 * evidence says the gate does not apply. Row absence no longer produces it
 * anywhere, so an absent row must never be rendered as "not required".
 *
 * The operational rule the whole slice turns on:
 *
 *   BLOCKED means the job is stopped.
 *   UNKNOWN means we do not know, and we are not starting until we do.
 *
 * Both withhold the work. Only one of them says something failed. An UNKNOWN
 * front is usually a problem in the platform's records rather than on site,
 * and the person who can clear it is often not the superintendent.
 *
 * THE BUMP THAT MADE THIS URGENT: `blockingReasons` now EMPTIES on fronts
 * that used to populate it. Those reasons were never established failures, so
 * they moved to `notEstablishedReasons`. A screen that renders the old lane
 * and ignores the new one shows a refusal with no reason at all — the exact
 * defect the backend slice existed to repair, reintroduced in the UI.
 */
import { platformGapDisplay } from './runtimeVocabulary';

export const WHAT_CAN_START_CONTRACT = 'what-can-start-2';

/* -------------------------------------------------------------------------
 * THE REALITY OF ONE GATE
 * ---------------------------------------------------------------------- */

const REALITIES = {
  ESTABLISHED_POSITIVE: {
    key: 'ESTABLISHED_POSITIVE',
    label: 'Established',
    tone: 'passed',
    meaning: 'Canonical evidence establishes this.',
  },
  ESTABLISHED_NEGATIVE: {
    key: 'ESTABLISHED_NEGATIVE',
    label: 'Established against',
    tone: 'failed',
    meaning: 'A real recorded condition says no.',
  },
  NOT_ESTABLISHED: {
    key: 'NOT_ESTABLISHED',
    // Never "failed" and never "clear". The whole point.
    label: 'Not established',
    tone: 'unknown',
    meaning: 'OMNΛS cannot establish this either way. Nothing is being asserted against the work.',
  },
  SOURCES_DISAGREE: {
    key: 'SOURCES_DISAGREE',
    label: 'Sources disagree',
    tone: 'conflict',
    meaning: 'Authoritative evidence disagrees and OMNΛS will not choose a winner.',
  },
  PLATFORM_GAP: {
    key: 'PLATFORM_GAP',
    label: 'OMNΛS is missing something',
    tone: 'gap',
    meaning: 'The platform lacks a model, an owner, a writer or a vocabulary needed to establish this.',
  },
  NOT_APPLICABLE: {
    key: 'NOT_APPLICABLE',
    label: 'Does not apply',
    tone: 'not-applicable',
    meaning: 'Explicit applicability evidence says this gate does not apply to this work.',
  },
};

export const realityDisplay = (key) =>
  REALITIES[key] || {
    key: String(key ?? 'NOT_ESTABLISHED'),
    label: 'A reality this view does not name',
    tone: 'unknown',
    meaning: 'The server described a state this view does not carry. It is shown verbatim.',
  };

/**
 * Classify one gate into exactly one of the six realities.
 *
 * PRECEDENCE, and it matters:
 *   1. `conflict` — the backend knows WHY this is unknown, and "sources
 *      disagree" is more actionable than a bare unknown.
 *   2. `platformGap` — likewise: it routes the work to whoever owns the
 *      missing column instead of leaving a superintendent to wonder.
 *   3. the plain status.
 *
 * `conflict` outranks `platformGap` because a disagreement is about the
 * world's records, while a gap is about ours; a gate carrying both is telling
 * us the sources we DO have disagree.
 *
 * Nothing here decides anything. `platformGap` explains an UNKNOWN and never
 * produces one, and no branch in this module reads it to change a status.
 */
export const gateReality = (gate) => {
  if (!gate) return null;
  const status = gate.status || null;
  if (status === 'PASSED') return REALITIES.ESTABLISHED_POSITIVE;
  if (status === 'FAILED') return REALITIES.ESTABLISHED_NEGATIVE;
  if (status === 'NOT_REQUIRED') return REALITIES.NOT_APPLICABLE;
  if (gate.conflict === true) return REALITIES.SOURCES_DISAGREE;
  if (gate.platformGap) return REALITIES.PLATFORM_GAP;
  if (status === 'UNKNOWN') return REALITIES.NOT_ESTABLISHED;
  return realityDisplay(status);
};

export const presentGateEvidence = (gate) => {
  if (!gate) return null;
  const reality = gateReality(gate);
  const gap = gate.platformGap ? platformGapDisplay(gate.platformGap) : null;
  return {
    gate: gate.gate || null,
    number: gate.number ?? null,
    status: gate.status || null,
    hard: gate.hard === true,
    detail: gate.detail || null,
    sourceTable: gate.sourceTable || null,
    sourceIds: gate.sourceIds || [],
    conflict: gate.conflict === true,
    platformGap: gate.platformGap || null,
    gap,
    reality,
  };
};

/* -------------------------------------------------------------------------
 * THE FRONT'S REASON LANES
 * ---------------------------------------------------------------------- */

export const LANE_COPY = {
  blocking: {
    key: 'blocking',
    title: 'Established blockers',
    lead: 'A recorded condition says no. Something has to change on the work itself.',
    tone: 'failed',
  },
  conditions: {
    key: 'conditions',
    title: 'Conditions on starting',
    lead: 'Known conditional state. Nothing here is blocking outright.',
    tone: 'attention',
  },
  notEstablished: {
    key: 'notEstablished',
    title: 'Not established',
    lead: 'OMNΛS cannot establish these either way. The work is withheld, and no field failure is being asserted.',
    tone: 'unknown',
  },
  conflict: {
    key: 'conflict',
    title: 'Sources disagree',
    lead: 'Authoritative evidence disagrees. OMNΛS will not pick a winner — a person has to read both sources.',
    tone: 'conflict',
  },
  platformGap: {
    key: 'platformGap',
    title: 'OMNΛS is missing information',
    lead: 'The digital execution model itself needs a record, an owner or a writer before this can be established.',
    tone: 'gap',
  },
};

export const WITHHELD_NOT_FAILED =
  'Work release: not cleared. That is a withholding, not a finding against the work.';

/**
 * Partition one work front into the lanes a reader needs.
 *
 * The lanes come from DIFFERENT payload fields on purpose, and none is
 * derived from another:
 *
 *   blocking        `blockingReasons`        established failures only
 *   conditions      `conditions`             known conditional state
 *   notEstablished  `notEstablishedReasons`  the lane the bump added
 *   conflict        evidence with conflict   structural flag, never prose
 *   platformGap     evidence with a gap      closed vocabulary
 *
 * An empty lane is omitted rather than rendered as an empty heading, but
 * `hasAnyReason` exists so a caller can tell "no reasons at all" from "this
 * front is cleared" — a non-cleared front with no reasons anywhere is a
 * defect worth surfacing, not a blank panel.
 */
export const presentFrontTruth = (front) => {
  if (!front) return null;
  const evidence = (front.evidence || []).map(presentGateEvidence).filter(Boolean);
  const blocking = front.blockingReasons || [];
  const conditions = front.conditions || [];
  // Read from the payload, never reconstructed from the other two lanes.
  const notEstablished = front.notEstablishedReasons || [];
  const conflicts = evidence.filter((gate) => gate.conflict);
  const gaps = evidence.filter((gate) => gate.platformGap);
  const cleared = front.canStart === true;
  return {
    instanceId: front.instanceId,
    status: front.status || null,
    canStart: front.canStart ?? null,
    governingConstraint: front.governingConstraint || null,
    // Additive in the bump; absent on older payloads, and an absent list is
    // not an empty one.
    unresolvedGates: front.unresolvedGates || [],
    unresolvedGatesStated: Object.prototype.hasOwnProperty.call(front, 'unresolvedGates'),
    missingFacts: front.missingFacts || [],
    evidence,
    lanes: [
      blocking.length ? { ...LANE_COPY.blocking, items: blocking } : null,
      conditions.length ? { ...LANE_COPY.conditions, items: conditions } : null,
      notEstablished.length ? { ...LANE_COPY.notEstablished, items: notEstablished } : null,
      conflicts.length ? { ...LANE_COPY.conflict, gates: conflicts, items: [] } : null,
      gaps.length ? { ...LANE_COPY.platformGap, gates: gaps, items: [] } : null,
    ].filter(Boolean),
    hasAnyReason:
      blocking.length + conditions.length + notEstablished.length + conflicts.length + gaps.length >
      0,
    // A front that is withheld with nothing in any lane. The old defect wore
    // exactly this shape, so it is named rather than shown as a blank panel.
    withheldWithoutReason:
      !cleared &&
      blocking.length + conditions.length + notEstablished.length + conflicts.length + gaps.length ===
        0,
    cleared,
    withheldNote: cleared ? null : WITHHELD_NOT_FAILED,
  };
};

/**
 * The headline reality for a whole front, for a badge beside its name.
 *
 * Conflict and platform gap outrank a bare UNKNOWN for the same reason they
 * do on a gate: the backend knows why, and saying only "unknown" throws that
 * away. An established failure outranks both — if something is actually
 * broken, that is the headline.
 */
export const frontHeadlineReality = (truth) => {
  if (!truth) return null;
  if (truth.cleared) return REALITIES.ESTABLISHED_POSITIVE;
  const hasBlocking = truth.lanes.some((lane) => lane.key === 'blocking');
  if (hasBlocking) return REALITIES.ESTABLISHED_NEGATIVE;
  if (truth.lanes.some((lane) => lane.key === 'conflict')) return REALITIES.SOURCES_DISAGREE;
  if (truth.lanes.some((lane) => lane.key === 'platformGap')) return REALITIES.PLATFORM_GAP;
  if (truth.lanes.some((lane) => lane.key === 'notEstablished')) return REALITIES.NOT_ESTABLISHED;
  return null;
};

/* -------------------------------------------------------------------------
 * PROJECT REALISM — blocked work and not-established work are different sets
 * ---------------------------------------------------------------------- */

export const REALISM_SPLIT_NOTE =
  'Not-established work is withheld for the same operational reason as blocked work. It is counted apart because nothing has been established against it — not because it is closer to starting.';

/**
 * `blockedWork` shrinks by exactly the `notEstablishedWork` set.
 *
 * A screen that renders only `blockedWork` will show the project improving
 * when nothing improved — truth merely became more precise. Both counts are
 * returned, and `notEstablishedStated` distinguishes an older payload that
 * carries no such lane from a current one where the lane is genuinely empty.
 */
export const presentRealismWorkSplit = (realism) => {
  if (!realism) return null;
  const blocked = realism.blockedWork || [];
  const stated = Object.prototype.hasOwnProperty.call(realism, 'notEstablishedWork');
  const notEstablished = realism.notEstablishedWork || [];
  return {
    blocked,
    blockedCount: blocked.length,
    notEstablished,
    notEstablishedCount: stated ? notEstablished.length : null,
    notEstablishedStated: stated,
    // Deliberately NOT summed into one headline number. The backend
    // publishes no combined interpretation, so neither does this.
    withheldNote: REALISM_SPLIT_NOTE,
  };
};

/* -------------------------------------------------------------------------
 * START_WORK REFUSAL — three classes, from the backend's own sentinels
 * ---------------------------------------------------------------------- */

const FAILED_MARKER = 'hard prerequisites have FAILED';
const NOT_ESTABLISHED_MARKER = 'hard prerequisites are NOT ESTABLISHED';

const REFUSAL_CLASSES = {
  ESTABLISHED_BLOCKER: {
    key: 'ESTABLISHED_BLOCKER',
    headline: 'Start not authorized — established blocker',
    meaning: 'A recorded condition says no. Something has to change before this can start.',
    tone: 'failed',
  },
  NOT_ESTABLISHED: {
    key: 'NOT_ESTABLISHED',
    headline: 'Start not authorized — not established',
    meaning:
      'OMNΛS cannot establish a hard prerequisite either way. No failure is being asserted against this work.',
    tone: 'unknown',
  },
  BOTH: {
    key: 'BOTH',
    headline: 'Start not authorized — a blocker and an unestablished prerequisite',
    meaning:
      'One prerequisite is established against this work and another cannot be established either way. They are different problems.',
    tone: 'failed',
  },
  UNCLASSIFIED: {
    key: 'UNCLASSIFIED',
    headline: 'Start not authorized',
    meaning:
      'The server refused without naming which class of prerequisite stopped it. Its own message is shown below, unchanged.',
    tone: 'unknown',
  },
};

export const REFUSAL_PROSE_CAVEAT =
  'Classified from the markers the runtime writes into its own refusal. The 409 payload carries no structured refusal class; where neither marker is present the message is shown verbatim rather than guessed at.';

/**
 * Classify a START_WORK refusal.
 *
 * The backend deliberately writes two named lanes into the one message a
 * person actually sees, so detecting those authored sentinels is reading the
 * runtime's own words — not inferring semantics from arbitrary prose.
 *
 * CONFLICT is deliberately NOT classified here. There is no gate status named
 * CONFLICT and it must never be inferred from prose; it is knowable only from
 * a gate's structural `conflict` flag, which this payload does not carry.
 * A conflicted front shows its conflict on the readiness surface instead.
 */
export const classifyStartRefusal = (message) => {
  const text = typeof message === 'string' ? message : '';
  const failed = text.includes(FAILED_MARKER);
  const unresolved = text.includes(NOT_ESTABLISHED_MARKER);
  if (failed && unresolved) return { ...REFUSAL_CLASSES.BOTH, message: text, caveat: null };
  if (failed) return { ...REFUSAL_CLASSES.ESTABLISHED_BLOCKER, message: text, caveat: null };
  if (unresolved) return { ...REFUSAL_CLASSES.NOT_ESTABLISHED, message: text, caveat: null };
  return { ...REFUSAL_CLASSES.UNCLASSIFIED, message: text, caveat: REFUSAL_PROSE_CAVEAT };
};

/* -------------------------------------------------------------------------
 * THE COPY THIS MODULE AUTHORS, and the guard that walks it
 * ---------------------------------------------------------------------- */

/**
 * Words that would collapse the five realities. "Failed" and "blocked" are
 * legitimate for an ESTABLISHED negative, so they are not banned outright —
 * what is banned is the vocabulary that turns an unknown or a platform gap
 * into somebody's fault.
 */
export const FORBIDDEN_TRUTH_WORDS = {
  blame: ['contractor failed', 'their fault', 'someone failed', 'crew failed', 'did not do'],
  falseClarity: ['all clear', 'no issues', 'nothing outstanding', 'ready to go', 'good to start'],
  falseNegative: ['scope failed', 'not required', 'no materials needed', 'no permit needed'],
};

export const authoredTruthCopy = () => [
  ...Object.values(REALITIES).flatMap((entry) => [entry.label, entry.meaning]),
  ...Object.values(LANE_COPY).flatMap((entry) => [entry.title, entry.lead]),
  ...Object.values(REFUSAL_CLASSES).flatMap((entry) => [entry.headline, entry.meaning]),
  WITHHELD_NOT_FAILED,
  REALISM_SPLIT_NOTE,
  REFUSAL_PROSE_CAVEAT,
];

export const findForbiddenTruthWords = (text) => {
  if (typeof text !== 'string') return [];
  const hits = [];
  Object.values(FORBIDDEN_TRUTH_WORDS).forEach((group) => {
    group.forEach((word) => {
      const pattern = new RegExp(`\\b${word.replace(/ /g, '\\s+')}\\b`, 'i');
      if (pattern.test(text)) hits.push(word);
    });
  });
  return hits;
};
